import React, { useEffect, useState } from "react";
import { getPageBySlug } from "../lib/cms";
import { BookOpen, Map, CheckSquare, Shield, Info, Users, Coffee, MapPin, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

export default function Teori() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const page = await getPageBySlug("teori");
        setPageData(page);
      } catch (err) {
        console.error("Error loading page data:", err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    if (pageData) {
      document.title = pageData.title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute("content", pageData.meta_description || "");
      }
    }
  }, [pageData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-light">
        <div className="animate-pulse text-brand-primary font-bold text-lg">Henter indhold...</div>
      </div>
    );
  }

  return (
    <div className="bg-brand-light min-h-screen py-12 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-bold uppercase tracking-wider mb-4">
            <Sparkles className="h-3.5 w-3.5" /> Teori & Prøvekrav
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-brand-dark tracking-tight mb-4">
            Teoriundervisning & Prøvekrav
          </h1>
          <p className="text-stone-600 text-base sm:text-lg max-w-3xl mx-auto leading-relaxed">
            Læs mere om vores nyindrettede teorilokale i Gug, køreteknisk anlæg (manøvre- og glatbane) samt kravene til teori- og køreprøven.
          </p>
        </div>

        {/* Quick Facts Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          <div className="bg-white p-5 rounded-2xl border border-stone-200/80 shadow-sm flex flex-col justify-between">
            <div className="h-10 w-10 rounded-xl bg-brand-primary/10 flex items-center justify-center text-brand-primary mb-3">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-bold text-brand-dark text-sm sm:text-base">Små hold (Max 8)</h3>
              <p className="text-stone-500 text-xs mt-1">Plads til den enkelte og masser af tid til spørgsmål.</p>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-stone-200/80 shadow-sm flex flex-col justify-between">
            <div className="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-600 mb-3">
              <Coffee className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-bold text-brand-dark text-sm sm:text-base">Snacks & Sodavand</h3>
              <p className="text-stone-500 text-xs mt-1">Altid kolde sodavand og lækre snacks til teoritimerne.</p>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-stone-200/80 shadow-sm flex flex-col justify-between">
            <div className="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-600 mb-3">
              <MapPin className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-bold text-brand-dark text-sm sm:text-base">Centralt i Gug</h3>
              <p className="text-stone-500 text-xs mt-1">Gugvej 186 D – nemt at komme til med bus eller cykel.</p>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-stone-200/80 shadow-sm flex flex-col justify-between">
            <div className="h-10 w-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-600 mb-3">
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-bold text-brand-dark text-sm sm:text-base">Høj beståelsesprocent</h3>
              <p className="text-stone-500 text-xs mt-1">Grundig og rolig forberedelse sikrer tryghed til prøven.</p>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-3xl border border-stone-200 shadow-xl overflow-hidden mb-12">
          {/* Hero Image */}
          {pageData.hero_image && pageData.hero_image[0] && (
            <div className="h-64 sm:h-96 w-full relative">
              <img
                src={`${pageData.hero_image[0]}?width=1200&quality=85&format=auto`}
                alt="Undervisningslokalet"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent flex items-end p-6 sm:p-10">
                <div>
                  <span className="px-3 py-1 bg-brand-primary text-white text-xs font-bold uppercase tracking-wider rounded-md mb-2 inline-block">Gugvej 186 D, kld. tv.</span>
                  <p className="text-white text-xl sm:text-3xl font-black tracking-tight">Vores nyindrettede teorilokale i Gug</p>
                </div>
              </div>
            </div>
          )}

          {/* Verbatim Content Area */}
          <div className="p-6 sm:p-10 lg:p-12 space-y-12">
            {/* Render HTML verbatim from CMS */}
            <div
              className="prose prose-stone max-w-none 
                prose-headings:font-sans prose-headings:text-brand-dark prose-headings:font-black
                prose-h1:text-3xl prose-h1:mb-6
                prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:border-b prose-h2:border-stone-100 prose-h2:pb-2
                prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-2 prose-h3:text-brand-primary
                prose-p:text-stone-600 prose-p:leading-relaxed prose-p:mb-4
                prose-ul:list-disc prose-ul:pl-5 prose-ul:mb-6 prose-ul:space-y-2
                prose-li:text-stone-600 prose-li:text-sm
                prose-strong:text-brand-dark prose-strong:font-bold"
              dangerouslySetInnerHTML={{ __html: pageData.content }}
            />

            {/* Extra visual sections for better layout */}
            <div className="border-t border-stone-100 pt-10 mt-10 grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Køreteknisk Anlæg Info Card */}
              <div className="bg-stone-50 rounded-2xl border border-stone-200/60 overflow-hidden hover:shadow-md transition-shadow duration-200">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/8426b648-fe3a-4547-bb02-98fcf24d83ce/9a7044-f36cd3a952f844c18f3cf8d50dfdfe42-mv2.jpg"
                  alt="Køreteknisk Anlæg"
                  className="h-48 w-full object-cover"
                />
                <div className="p-6 space-y-3">
                  <h4 className="font-bold text-brand-dark text-base flex items-center gap-2">
                    <Map className="h-5 w-5 text-brand-primary" />
                    Køreteknisk Anlæg
                  </h4>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    Alt manøvre- og glatbanekørsel foregår på Køreteknisk Anlæg i Viborg eller Støvrings Køreteknisk Anlæg. Vi sørger for, at I bliver fragtet frem og tilbage med bus.
                  </p>
                </div>
              </div>

              {/* Tryghed Card */}
              <div className="bg-stone-50 rounded-2xl border border-stone-200/60 overflow-hidden hover:shadow-md transition-shadow duration-200">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/8426b648-fe3a-4547-bb02-98fcf24d83ce/9a7044-b0e5b3919f4e427daa0e2bdd7a21306d-mv2-d-5184-3456-s-4-2.jpg"
                  alt="Undervisningsmiljø"
                  className="h-48 w-full object-cover"
                />
                <div className="p-6 space-y-3">
                  <h4 className="font-bold text-brand-dark text-base flex items-center gap-2">
                    <Shield className="h-5 w-5 text-brand-primary" />
                    Trygt & Positivt Miljø
                  </h4>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    Der er fokus på det positive undervisningsmiljø, som danner grundlag for den bedste læring. Undervisningen tilrettelægges efter den enkelte elev.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="bg-brand-dark text-white rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden shadow-xl">
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-primary/10 rounded-full blur-3xl -mr-20 -mt-20"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-primary/10 rounded-full blur-3xl -ml-20 -mb-20"></div>
          
          <div className="relative z-10 max-w-2xl mx-auto space-y-6">
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white">Klar til at starte din rejse?</h2>
            <p className="text-stone-300 text-sm sm:text-base">
              Se vores kommende holdstart-datoer og priser, eller kontakt Carina direkte for at høre mere om teoriundervisningen.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-2">
              <Link
                to="/priser"
                className="inline-flex items-center justify-center px-6 py-3 bg-brand-primary text-white font-bold rounded-xl hover:bg-brand-primary-hover transition-colors duration-150 text-sm shadow-lg"
              >
                Se holdstarter & priser
              </Link>
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center px-6 py-3 bg-white/10 text-white font-bold rounded-xl hover:bg-white/20 transition-colors duration-150 text-sm border border-white/10"
              >
                Kontakt Carina
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
